import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Database, CheckCircle, AlertCircle } from 'lucide-react';
import { amazonIndexCheckerService } from '@/services/amazonIndexCheckerService';

interface BackfillResult {
  processed: number;
  updated: number;
  errors: number;
}

const DashboardCacheBackfill: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<BackfillResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleBackfill = async () => {
    setIsRunning(true);
    setResult(null);
    setError(null);

    try {
      console.log('🔄 Starting dashboard cache backfill...');
      const backfillResult = await amazonIndexCheckerService.backfillDashboardCache();
      setResult(backfillResult);
      console.log('✅ Backfill complete:', backfillResult);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('❌ Backfill error:', err);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="space-y-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border">
      <div className="flex items-center gap-2">
        <Database className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          Dashboard Cache Backfill
        </h3>
      </div>
      
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Run this once to populate the dashboard cache for existing ASINs. 
        This will make the dashboard load much faster.
      </p>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {result && (
        <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-700 dark:text-green-300">
            <div className="space-y-1">
              <div><strong>Backfill Complete!</strong></div>
              <div>• Processed: {result.processed} ASINs</div>
              <div>• Updated: {result.updated} cache entries</div>
              <div>• Errors: {result.errors}</div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Button 
        onClick={handleBackfill} 
        disabled={isRunning}
        className="w-full"
      >
        {isRunning ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Running Backfill...
          </>
        ) : (
          <>
            <Database className="h-4 w-4 mr-2" />
            Run Dashboard Cache Backfill
          </>
        )}
      </Button>

      {isRunning && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          This may take a few minutes depending on how many ASINs you have. 
          Check the browser console for progress updates.
        </p>
      )}
    </div>
  );
};

export default DashboardCacheBackfill;